/**
 * Handles anchor links to theme sections, both same-page ("#category_grid_9k2xqw")
 * and cross-page ("/#category_grid_9k2xqw" from the sitewide footer).
 *
 * This theme moves scrolling to `.page-wrapper` on desktop (`html`/`body` get
 * `overflow: hidden`), so the browser's native fragment-navigation scroll
 * doesn't reliably reach the target. `scrollIntoView` walks up to whichever
 * ancestor actually scrolls, so it works regardless of that layout — but
 * `behavior: 'smooth'` silently no-ops on this nested scroll container, so
 * this always scrolls instantly rather than animating.
 *
 * Shopify renders each JSON-template section inside a wrapper whose id is
 * `shopify-section-{scope}__{section-id}` (the `{scope}` segment is a
 * store/template-specific value that can change, e.g. if the template is
 * duplicated). Anchor links here use the stable section id on its own
 * (e.g. "#category_grid_9k2xqw"), so we match any wrapper whose id ends
 * with it rather than hardcoding the full generated id.
 */
/**
 * @param {string} id
 * @returns {Element | null}
 */
function resolveAnchorTarget(id) {
  return document.getElementById(id) || document.querySelector(`[id$="__${id}"]`);
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const link = event.target.closest('a[href]');
  if (!(link instanceof HTMLAnchorElement)) return;

  const raw = link.getAttribute('href') || '';
  if (!raw.includes('#')) return;

  // Matches both "#section-id" and "/#section-id". The second form matters:
  // the footer links to homepage sections, and the footer is on every page —
  // a bare "#section-id" there is dead everywhere except the homepage.
  const url = new URL(link.href, location.href);

  // Only intercept links pointing at the page we are already on. A "/#story"
  // clicked from a product page must be allowed to navigate; jumpToInitialHash
  // below then does the scrolling once the homepage has loaded.
  if (url.pathname !== location.pathname || url.search !== location.search) return;

  const id = url.hash.slice(1);
  if (!id) return;

  const target = resolveAnchorTarget(id);
  if (!target) return;

  event.preventDefault();
  target.scrollIntoView({ behavior: 'instant', block: 'start' });
});

/**
 * Handles landing on the page with a hash already in the URL (e.g. a shared
 * link pointing at "#panic_slider_m00dz1"), not just in-page clicks. Waits a
 * couple frames so above-the-fold layout (hero, fonts) has settled before
 * jumping, since scrolling immediately on DOMContentLoaded can land short.
 */
function jumpToInitialHash() {
  if (!location.hash || location.hash.length < 2) return;

  const target = resolveAnchorTarget(location.hash.slice(1));
  if (!target) return;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'instant', block: 'start' });
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', jumpToInitialHash);
} else {
  jumpToInitialHash();
}
